import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight, BookOpen, FileText, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import DOMPurify from "dompurify";
import { PanelHeader } from "@/components/settings/SettingsUI";

const API = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

// Defense-in-depth: every `dangerouslySetInnerHTML` payload in this
// file is the output of `formatInline()` which already HTML-escapes
// raw text and rejects non-http(s)/mailto link protocols. We add a
// DOMPurify sanitization pass on top so any future regression in
// `formatInline` (or a Wiki author intentionally exploiting it) cannot
// inject script/event-handler attributes into the live DOM.
const SAFE_TAGS = ["a", "code", "strong", "em", "br"];
const SAFE_ATTRS = ["href", "class", "target", "rel"];
const sanitize = (html) => ({
  __html: DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SAFE_TAGS,
    ALLOWED_ATTR: SAFE_ATTRS,
    ALLOW_DATA_ATTR: false,
  }),
});

const FILE_DESCRIPTIONS = {
  "README.md": "Index & wiki overview",
  "architecture.md": "Stack, services, data flow",
  "schema.md": "MongoDB collections & relationships",
  "api.md": "All HTTP endpoints",
  "features.md": "Shipped features & status",
  "integrations.md": "Third-party services & env vars",
  "changelog.md": "Dated change log",
  "known-issues.md": "Open & resolved issues",
  "runbook.md": "Deploy, rollback, ops",
};

/**
 * Minimal GFM-subset Markdown renderer — headings, paragraphs, lists,
 * inline code, fenced code blocks, blockquotes, tables, links. Avoids
 * pulling in a ~180kb react-markdown dep for an internal read-only page.
 * Handles the formatting actually used in our wiki files; anything
 * beyond that renders as literal text (acceptable for dev docs).
 */
function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  let inCode = false;
  let codeLines = [];
  let inTable = false;
  let tableRows = [];
  let listItems = null;
  let listType = null;

  const flushList = () => {
    if (listItems) {
      const Tag = listType === "ol" ? "ol" : "ul";
      out.push(
        <Tag key={`list-${out.length}`} className={`${listType === "ol" ? "list-decimal" : "list-disc"} ml-5 mb-2 space-y-1 text-slate-700 text-sm`}>
          {listItems.map((item) => <li key={`li-${item}`} dangerouslySetInnerHTML={sanitize(formatInline(item))} />)}
        </Tag>
      );
      listItems = null;
      listType = null;
    }
  };

  const flushTable = () => {
    if (inTable && tableRows.length) {
      const [header, _sep, ...body] = tableRows;
      const headerCells = header.split("|").slice(1, -1).map(s => s.trim());
      out.push(
        <div key={`table-${out.length}`} className="my-3 overflow-x-auto">
          <table className="text-xs border border-slate-200 rounded">
            <thead className="bg-slate-50">
              <tr>{headerCells.map((h) => (
                <th key={`h-${h}`} className="px-2 py-1.5 text-left font-semibold text-slate-700 border-b border-slate-200"
                    dangerouslySetInnerHTML={sanitize(formatInline(h))} />
              ))}</tr>
            </thead>
            <tbody>
              {body.map((row) => {
                const cells = row.split("|").slice(1, -1).map(s => s.trim());
                return <tr key={`row-${row}`} className="border-b border-slate-100">
                  {cells.map((c, ci) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <td key={`cell-${ci}-${c.slice(0,8)}`} className="px-2 py-1.5 text-slate-600"
                        dangerouslySetInnerHTML={sanitize(formatInline(c))} />
                  ))}
                </tr>;
              })}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      inTable = false;
    }
  };

  const formatInline = (text) => {
    let t = text;
    // Escape HTML first
    t = t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    // Then decode things we intentionally allowed through
    // Inline code
    t = t.replace(/`([^`]+)`/g, '<code class="bg-slate-100 text-slate-800 px-1 py-0.5 rounded text-[11px] font-mono">$1</code>');
    // Bold
    t = t.replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-800">$1</strong>');
    // Italic
    t = t.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    // Links [text](url) — sanitize href to allow only http/https/mailto.
    // Without validation, a Wiki author (or a compromised editor) could
    // ship `[Help](javascript:alert(1))` and execute arbitrary JS via
    // the dangerouslySetInnerHTML render path. (H6 fix.)
    t = t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_m, label, href) => {
      const trimmed = String(href).trim();
      const safe = /^(https?:\/\/|mailto:|#|\/)/i.test(trimmed) && !/^javascript:/i.test(trimmed);
      if (!safe) {
        // Drop the link entirely — show only the label as plain text.
        return label;
      }
      // Escape any embedded quotes in the href to defeat attribute
      // injection (`"><img onerror=...`).
      const escapedHref = trimmed.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
      return `<a href="${escapedHref}" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">${label}</a>`;
    });
    return t;
  };

  while (i < lines.length) {
    const raw = lines[i];

    // Fenced code blocks
    if (raw.startsWith("```")) {
      if (!inCode) {
        flushList(); flushTable();
        inCode = true; codeLines = [];
      } else {
        out.push(
          <pre key={`code-${out.length}`} className="bg-slate-900 text-slate-100 rounded p-3 overflow-x-auto text-[11px] font-mono mb-3">
            <code>{codeLines.join("\n")}</code>
          </pre>
        );
        inCode = false; codeLines = [];
      }
      i++; continue;
    }
    if (inCode) { codeLines.push(raw); i++; continue; }

    // Tables — pipe-delimited rows with a `---|---` separator on row 2
    if (raw.trim().startsWith("|") && raw.trim().endsWith("|")) {
      flushList();
      inTable = true;
      tableRows.push(raw);
      i++; continue;
    } else if (inTable) {
      flushTable();
    }

    // Headings
    const h = raw.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      flushList();
      const level = h[1].length;
      const txt = h[2];
      const sizes = { 1: "text-xl font-bold mt-4 mb-2", 2: "text-lg font-semibold mt-4 mb-2 text-slate-800 border-b border-slate-200 pb-1", 3: "text-base font-semibold mt-3 mb-1.5 text-slate-700", 4: "text-sm font-semibold mt-2 mb-1 text-slate-600" };
      const Tag = `h${level}`;
      out.push(<Tag key={`h-${i}`} className={sizes[level]} dangerouslySetInnerHTML={sanitize(formatInline(txt))} />);
      i++; continue;
    }

    // Blockquotes
    if (raw.startsWith("> ")) {
      flushList();
      out.push(
        <blockquote key={`q-${i}`} className="border-l-2 border-slate-300 pl-3 italic text-xs text-slate-500 my-2"
                    dangerouslySetInnerHTML={sanitize(formatInline(raw.slice(2)))} />
      );
      i++; continue;
    }

    // Lists
    const ulMatch = raw.match(/^(\s*)-\s+(.*)$/);
    const olMatch = raw.match(/^(\s*)\d+\.\s+(.*)$/);
    if (ulMatch || olMatch) {
      const match = ulMatch || olMatch;
      const newType = ulMatch ? "ul" : "ol";
      if (!listItems || listType !== newType) {
        flushList();
        listItems = []; listType = newType;
      }
      listItems.push(match[2]);
      i++; continue;
    }

    // Horizontal rule
    if (raw.trim() === "---") {
      flushList();
      out.push(<hr key={`hr-${i}`} className="my-4 border-slate-200" />);
      i++; continue;
    }

    // Blank line → flush
    if (raw.trim() === "") {
      flushList();
      i++; continue;
    }

    // Plain paragraph
    flushList();
    out.push(<p key={`p-${i}`} className="text-sm text-slate-700 leading-relaxed mb-2"
                dangerouslySetInnerHTML={sanitize(formatInline(raw))} />);
    i++;
  }
  flushList();
  flushTable();
  return out;
}


export default function WikiSection({ expanded, toggle }) {
  const [files, setFiles] = useState([]);
  const [selected, setSelected] = useState("README.md");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!expanded) return;
    API.get("/wiki").then(r => setFiles(r.data.files || []))
      .catch(e => setError(e.message));
  }, [expanded]);

  useEffect(() => {
    if (!expanded || !selected) return;
    setLoading(true);
    setError("");
    API.get(`/wiki/${selected}`)
      .then(r => setContent(r.data.content || ""))
      .catch(e => {
        setError(e.response?.data?.detail || e.message);
        setContent("");
      })
      .finally(() => setLoading(false));
  }, [selected, expanded]);

  return (
    <div data-testid="wiki-section">
      <PanelHeader icon={BookOpen} title="Wiki" hint="Living documentation for the app" testId="toggle-wiki" />
      {expanded && (
        <div className="px-5 py-4 flex gap-4 min-h-[420px]">
          {/* File list */}
          <aside className="w-56 shrink-0 border-r border-slate-200 pr-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Docs</div>
            <div className="space-y-0.5">
              {files.map(f => (
                <button
                  key={f.name}
                  onClick={() => setSelected(f.name)}
                  className={`w-full text-left px-2 py-1.5 rounded text-xs flex items-start gap-2 transition-colors ${
                    selected === f.name
                      ? "bg-slate-800 text-white"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                  data-testid={`wiki-file-${f.name}`}
                >
                  <FileText className="w-3 h-3 mt-0.5 shrink-0 opacity-60" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{f.name}</div>
                    <div className={`text-[10px] truncate ${selected === f.name ? "text-slate-300" : "text-slate-400"}`}>
                      {FILE_DESCRIPTIONS[f.name] || ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-slate-400">
              {files.length > 0 && (
                <Badge variant="outline" className="text-[9px]">
                  {files.reduce((s, f) => s + f.size_bytes, 0).toLocaleString()} bytes total
                </Badge>
              )}
            </div>
          </aside>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {loading ? (
              <div className="flex items-center justify-center h-full text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : error ? (
              <div className="flex items-start gap-2 text-rose-600 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            ) : (
              <div className="prose prose-slate max-w-none" data-testid="wiki-content">
                {renderMarkdown(content)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
